import { buildRootTabSearch, getRootTabFromSearch } from "./navigationState";
import { createFishipediaHistoryTransaction } from "./fishipediaRestoreHistory";

export const FISHIPEDIA_RETURN_CONTEXT_KEY = "fishipediaReturnContext";
export const FISHIPEDIA_RESTORE_CONTEXT_KEY = "fishipediaRestoreContext";
export const FISHIPEDIA_RESTORED_STACK_KEY = "fishipediaRestoredStack";
export const FISHIPEDIA_TERMINAL_MAP_PARENT_KEY =
  "fishipediaTerminalMapParent";

const CONTEXT_VERSION = 3;
const MAP_SPECIES_TYPE = "map-species";
const POST_INFO_TYPE = "post-info";
const MIN_MAP_ZOOM = 2;
const MAX_MAP_ZOOM = 18;
const claimedRestoreAttempts = new Set();

function createRandomIdentifier() {
  const cryptoApi =
    typeof window !== "undefined" ? window.crypto : undefined;

  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof cryptoApi?.getRandomValues === "function") {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0")
  ).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}

// This token intentionally exists only for the lifetime of this in-memory module.
// A full document load evaluates the module again and invalidates old route state.
const DOCUMENT_INSTANCE_TOKEN = createRandomIdentifier();

function normalizeId(value) {
  return String(value || "").trim();
}

function isObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasValidReturnOwnership(context) {
  return (
    context.documentToken === DOCUMENT_INSTANCE_TOKEN &&
    typeof context.restoreAttemptId === "string" &&
    context.restoreAttemptId.trim().length > 0
  );
}

function buildReturnOwnership() {
  return {
    documentToken: DOCUMENT_INSTANCE_TOKEN,
    restoreAttemptId: createRandomIdentifier(),
  };
}

function buildFeedFallback() {
  return {
    action: "fallback-feed",
    to: "/",
    options: { replace: true },
  };
}

function applyStackState(state, stackKey, stack) {
  const nextState = { ...(state || {}) };
  if (stack.length > 0) nextState[stackKey] = [...stack];
  else delete nextState[stackKey];
  return Object.keys(nextState).length > 0 ? nextState : null;
}

function buildTerminalMapParentState(parentState, context) {
  if (context?.type !== POST_INFO_TYPE || context.surface !== "map") {
    return parentState;
  }
  return {
    ...(parentState || {}),
    [FISHIPEDIA_TERMINAL_MAP_PARENT_KEY]: {
      version: 1,
      documentToken: DOCUMENT_INSTANCE_TOKEN,
      restoreAttemptId: context.restoreAttemptId,
      selectedSpot: context.selectedSpot,
    },
  };
}

function buildRootLocation(tab) {
  return {
    pathname: "/",
    search: buildRootTabSearch("", tab),
    hash: "",
  };
}

function isValidMapState(mapState) {
  const lat = mapState?.lat;
  const lng = mapState?.lng;
  const zoom = mapState?.zoom;
  return (
    typeof lat === "number" &&
    Number.isFinite(lat) &&
    lat >= -90 &&
    lat <= 90 &&
    typeof lng === "number" &&
    Number.isFinite(lng) &&
    lng >= -180 &&
    lng <= 180 &&
    typeof zoom === "number" &&
    Number.isFinite(zoom) &&
    zoom >= MIN_MAP_ZOOM &&
    zoom <= MAX_MAP_ZOOM
  );
}

function buildSpotSnapshot(selectedSpot) {
  const lat = selectedSpot?.lat;
  const lng = selectedSpot?.lng;
  if (
    !isObject(selectedSpot) ||
    typeof lat !== "number" ||
    !Number.isFinite(lat) ||
    lat < -90 ||
    lat > 90 ||
    typeof lng !== "number" ||
    !Number.isFinite(lng) ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }

  return {
    id: normalizeId(selectedSpot.id) || null,
    name: normalizeId(selectedSpot.name),
    location: normalizeId(selectedSpot.location),
    lat,
    lng,
    isPrivate: selectedSpot.isPrivate === true,
  };
}

export function createMapSpeciesFishipediaReturnContext({
  fishName,
  mapState,
  historyLocation = null,
}) {
  const normalizedFishName = normalizeId(fishName);
  if (!normalizedFishName || !isValidMapState(mapState)) {
    return null;
  }

  const ownership = buildReturnOwnership();
  const returnTo = buildRootLocation("map");
  const historyTransaction = createFishipediaHistoryTransaction({
    location: historyLocation,
    restoreAttemptId: ownership.restoreAttemptId,
    returnTo,
    stackKey: "mapOverlayStack",
    parentStack: ["weather-overlay"],
    childId: "weather-fish",
  });

  return {
    version: CONTEXT_VERSION,
    type: MAP_SPECIES_TYPE,
    ...ownership,
    fishName: normalizedFishName,
    mapState: {
      lat: mapState.lat,
      lng: mapState.lng,
      zoom: mapState.zoom,
      environment: normalizeId(mapState?.environment) || null,
    },
    returnTo,
    historyTransaction,
  };
}

export function createPostInfoFishipediaReturnContext({
  post,
  surface = "app",
  selectedSpot = null,
  returnTab = "feed",
  historyLocation = null,
}) {
  const postId = normalizeId(post?.id);
  const normalizedSurface = surface === "map" ? "map" : "app";
  const normalizedReturnTab = normalizedSurface === "map" ? "map" : returnTab;

  const spot = normalizedSurface === "map" ? buildSpotSnapshot(selectedSpot) : null;
  if (!postId || !isObject(post) || (normalizedSurface === "map" && !spot)) {
    return null;
  }

  const ownership = buildReturnOwnership();
  const returnTo = buildRootLocation(normalizedReturnTab);
  const isMap = normalizedSurface === "map";
  const historyTransaction = createFishipediaHistoryTransaction({
    location: historyLocation,
    restoreAttemptId: ownership.restoreAttemptId,
    returnTo,
    stackKey: isMap ? "mapOverlayStack" : "appOverlayStack",
    parentStack: isMap ? ["spot-legend"] : [],
    childId: isMap ? "spot-post-info" : "info-popup",
  });

  return {
    version: CONTEXT_VERSION,
    type: POST_INFO_TYPE,
    ...ownership,
    surface: normalizedSurface,
    postId,
    selectedSpot: spot,
    returnTo,
    historyTransaction,
  };
}

function resolveValidContext(state) {
  const context = state?.[FISHIPEDIA_RETURN_CONTEXT_KEY];
  if (!isObject(context) || context.version !== CONTEXT_VERSION) return null;
  if (!hasValidReturnOwnership(context)) return null;
  if (!isObject(context.returnTo) || context.returnTo.pathname !== "/") return null;
  if (context.returnTo.hash) return null;

  const returnTab = getRootTabFromSearch(context.returnTo.search);

  if (context.type === MAP_SPECIES_TYPE) {
    if (
      returnTab !== "map" ||
      !normalizeId(context.fishName) ||
      !isValidMapState(context.mapState)
    ) {
      return null;
    }
    return {
      ...context,
      fishName: normalizeId(context.fishName),
      mapState: {
        ...context.mapState,
        lat: context.mapState.lat,
        lng: context.mapState.lng,
        zoom: context.mapState.zoom,
      },
      returnTo: buildRootLocation("map"),
    };
  }

  if (context.type === POST_INFO_TYPE) {
    const postId = normalizeId(context.postId);
    if (!postId) return null;

    if (context.surface === "map") {
      if (returnTab !== "map" || !buildSpotSnapshot(context.selectedSpot)) return null;
    } else if (context.surface !== "app" || returnTab !== "feed") {
      return null;
    }

    return {
      ...context,
      postId,
      selectedSpot:
        context.surface === "map" ? buildSpotSnapshot(context.selectedSpot) : null,
      returnTo: buildRootLocation(returnTab),
    };
  }

  return null;
}

export function resolveFishipediaCloseNavigation(state, currentEntryKey = "") {
  const context = resolveValidContext(state);

  if (!context) {
    return buildFeedFallback();
  }

  const isMapSpecies = context.type === MAP_SPECIES_TYPE;
  const isMapPost = context.type === POST_INFO_TYPE && context.surface === "map";
  const stackKey = isMapSpecies || isMapPost ? "mapOverlayStack" : "appOverlayStack";
  const { childStack } = getFishipediaRestoreStackPlan(context);
  const action = isMapSpecies
    ? "restore-map-species"
    : isMapPost
      ? "restore-map-post-info"
      : "restore-feed-post-info";

  return {
    action,
    to: context.returnTo,
    options: {
      replace: true,
      state: {
        [FISHIPEDIA_RESTORE_CONTEXT_KEY]: context,
        ...(childStack.length > 0 ? { [stackKey]: childStack } : {}),
        [FISHIPEDIA_RESTORED_STACK_KEY]: {
          attemptId: context.restoreAttemptId,
          stackKey,
          action,
          sourceEntryKey: normalizeId(currentEntryKey) || null,
        },
      },
    },
  };
}

export function consumeFishipediaRestoreContext(state, stateOverrides = {}) {
  const nextState = { ...(state || {}), ...stateOverrides };
  delete nextState[FISHIPEDIA_RESTORE_CONTEXT_KEY];
  delete nextState[FISHIPEDIA_RESTORED_STACK_KEY];
  return Object.keys(nextState).length > 0 ? nextState : null;
}

export function getFishipediaTerminalMapParent(state) {
  const marker = state?.[FISHIPEDIA_TERMINAL_MAP_PARENT_KEY];
  const selectedSpot = buildSpotSnapshot(marker?.selectedSpot);
  if (
    !isObject(marker) ||
    marker.version !== 1 ||
    marker.documentToken !== DOCUMENT_INSTANCE_TOKEN ||
    !normalizeId(marker.restoreAttemptId) ||
    !selectedSpot ||
    !Array.isArray(state?.mapOverlayStack) ||
    state.mapOverlayStack.length !== 1 ||
    state.mapOverlayStack[0] !== "spot-legend"
  ) {
    return null;
  }
  return {
    restoreAttemptId: normalizeId(marker.restoreAttemptId),
    selectedSpot,
  };
}

export function consumeFishipediaTerminalMapParent(state) {
  if (!state || typeof state !== "object") return state ?? null;
  const nextState = { ...state };
  delete nextState[FISHIPEDIA_TERMINAL_MAP_PARENT_KEY];
  return Object.keys(nextState).length > 0 ? nextState : null;
}

export function getFishipediaRestoreStackPlan(context) {
  const isMapSpecies = context?.type === MAP_SPECIES_TYPE;
  const isMapPost =
    context?.type === POST_INFO_TYPE && context?.surface === "map";

  if (isMapSpecies) {
    return {
      stackKey: "mapOverlayStack",
      parentStack: ["weather-overlay"],
      childStack: ["weather-overlay", "weather-fish"],
    };
  }

  if (isMapPost) {
    return {
      stackKey: "mapOverlayStack",
      parentStack: ["spot-legend"],
      childStack: ["spot-legend", "spot-post-info"],
    };
  }

  if (context?.type === POST_INFO_TYPE && context?.surface === "app") {
    return {
      stackKey: "appOverlayStack",
      parentStack: [],
      childStack: ["info-popup"],
    };
  }

  return null;
}

export function buildFishipediaRestoreHistoryStates(
  state,
  stateOverrides = {}
) {
  const context = getFishipediaRestoreContext(state);
  const plan = getFishipediaRestoreStackPlan(context);
  const marker = state?.[FISHIPEDIA_RESTORED_STACK_KEY];
  if (
    !context ||
    !plan ||
    marker?.attemptId !== context.restoreAttemptId ||
    marker?.stackKey !== plan.stackKey
  ) {
    return null;
  }

  const consumedState = consumeFishipediaRestoreContext(state, stateOverrides);
  const parentState = applyStackState(
    consumedState,
    plan.stackKey,
    plan.parentStack
  );
  return {
    context,
    ...plan,
    parentState,
    terminalParentState: buildTerminalMapParentState(parentState, context),
    childState: applyStackState(consumedState, plan.stackKey, plan.childStack),
  };
}

export function claimFishipediaRestoreAttempt(context) {
  const attemptId = context?.restoreAttemptId;
  if (
    !isObject(context) ||
    !hasValidReturnOwnership(context) ||
    typeof attemptId !== "string" ||
    !attemptId.trim()
  ) {
    return false;
  }
  if (claimedRestoreAttempts.has(attemptId)) return false;
  claimedRestoreAttempts.add(attemptId);
  return true;
}

export function getFishipediaRestoreContext(state) {
  const context = state?.[FISHIPEDIA_RESTORE_CONTEXT_KEY];
  if (!isObject(context)) return null;

  return resolveValidContext({
    [FISHIPEDIA_RETURN_CONTEXT_KEY]: context,
  });
}

export function resolveCurrentFishipediaPost(posts, context) {
  if (context?.type !== POST_INFO_TYPE) return null;
  return (
    (Array.isArray(posts) ? posts : []).find(
      (post) => normalizeId(post?.id) === context.postId
    ) || null
  );
}

export function resolveFishipediaPostRestoreReadiness(
  authoritativeResult,
  context
) {
  if (context?.type !== POST_INFO_TYPE || !authoritativeResult) {
    return { status: "pending", post: null };
  }

  if (authoritativeResult.status === "found") {
    const post = authoritativeResult.post;
    return normalizeId(post?.id) === context.postId
      ? { status: "found", post }
      : { status: "failed", post: null };
  }

  if (
    authoritativeResult.status === "missing" ||
    authoritativeResult.status === "failed"
  ) {
    return { status: authoritativeResult.status, post: null };
  }

  return { status: "failed", post: null };
}
